import { useOrgHref } from '@tinycld/core/lib/org-routes'
import { useThemeColor } from '@tinycld/core/lib/use-app-theme'
import {
    Drawer,
    DrawerBackdrop,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
} from '@tinycld/core/ui/drawer'
import { router } from 'expo-router'
import { ArrowLeft, X } from 'lucide-react-native'
import { Pressable, Text, View } from 'react-native'
import { useHelpStore } from '../../lib/help/store'
import { useHelpGroupForPackage, useHelpTopic } from '../../lib/help/use-help-topics'
import { HelpTopicView } from './HelpTopicView'
import { PackageTopicList } from './PackageTopicList'

function ReadAllLink({ isVisible, onPress }: { isVisible: boolean; onPress: () => void }) {
    if (!isVisible) return null
    return (
        <Pressable onPress={onPress} accessibilityRole="link">
            <Text className="text-xs text-muted-foreground hover:underline">
                Read all tinycld help →
            </Text>
        </Pressable>
    )
}

export function HelpDrawer({ hasReadAllLink = true }: { hasReadAllLink?: boolean }) {
    const isOpen = useHelpStore(s => s.isOpen)
    const mode = useHelpStore(s => s.mode)
    const topicId = useHelpStore(s => s.topicId)
    const pkgSlug = useHelpStore(s => s.pkgSlug)
    const cameFrom = useHelpStore(s => s.cameFrom)
    const close = useHelpStore(s => s.close)
    const back = useHelpStore(s => s.back)

    const topic = useHelpTopic(topicId)
    const pkgGroup = useHelpGroupForPackage(pkgSlug)
    const muted = useThemeColor('muted-foreground')
    const orgHref = useOrgHref()

    const showBackArrow = mode === 'topic' && cameFrom !== null

    const title = resolveTitle({
        mode,
        topicTitle: topic?.title,
        packageLabel: pkgGroup?.packageName,
    })

    const onReadAll = () => {
        router.push(orgHref('help'))
        close()
    }

    return (
        <Drawer isOpen={isOpen} onClose={close} anchor="right" size="md">
            <DrawerBackdrop />
            <DrawerContent>
                <DrawerHeader>
                    <View className="flex-row items-center justify-between flex-1">
                        <View className="flex-row items-center flex-1">
                            {showBackArrow && (
                                <Pressable
                                    onPress={back}
                                    accessibilityRole="button"
                                    accessibilityLabel="Back to package help"
                                    className="mr-2"
                                >
                                    <ArrowLeft size={18} color={muted} />
                                </Pressable>
                            )}
                            <Text className="text-lg font-semibold text-foreground">{title}</Text>
                        </View>
                        <DrawerCloseButton onPress={close}>
                            <X size={20} color={muted} />
                        </DrawerCloseButton>
                    </View>
                </DrawerHeader>
                <DrawerBody>
                    <DrawerBodyContent
                        mode={mode}
                        topicId={topicId}
                        topic={topic}
                        pkgSlug={pkgSlug}
                    />
                </DrawerBody>
                <DrawerFooter>
                    <ReadAllLink isVisible={hasReadAllLink} onPress={onReadAll} />
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    )
}

interface BodyProps {
    mode: 'topic' | 'package'
    /** The requested id, so an unresolved topic can be told from none at all. */
    topicId: string | null
    topic: ReturnType<typeof useHelpTopic>
    pkgSlug: string | null
}

function DrawerBodyContent({ mode, topicId, topic, pkgSlug }: BodyProps) {
    if (mode === 'package' && pkgSlug) {
        return <PackageTopicList pkgSlug={pkgSlug} />
    }
    if (topic) {
        return <HelpTopicView topic={topic} showTitle={false} />
    }
    // A topic WAS asked for but did not resolve — it documents a setting this
    // deployment does not administer, so it is filtered out. Saying "none
    // selected" here would be a lie the reader cannot act on; a link from
    // another topic is the usual way to arrive.
    if (topicId) {
        return (
            <Text className="text-sm text-muted-foreground">
                That topic covers a setting your hosting provider manages, so it does not apply to
                this deployment.
            </Text>
        )
    }
    return <Text className="text-sm text-muted-foreground">No help topic selected.</Text>
}

interface TitleArgs {
    mode: 'topic' | 'package'
    topicTitle: string | undefined
    packageLabel: string | undefined
}

function resolveTitle({ mode, topicTitle, packageLabel }: TitleArgs): string {
    if (mode === 'topic') return topicTitle ?? 'Help'
    if (packageLabel) return `${packageLabel} help`
    return 'Help'
}
